import { Line } from '@react-three/drei';
import { ThreeEvent, useLoader } from '@react-three/fiber';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { DoubleSide, FrontSide, Group } from 'three';
import { mergeBufferGeometries, SVGLoader as ThreeSVGLoader } from 'three-stdlib';
import { ITexture } from '../../@types/ContextProviderRelated';
import { EInteractionMode } from '../../@types/Enums';
import useDraggableMesh from '../../hooks/useDraggableMesh';
import usePointerKind from '../../hooks/usePointerKind';
import { MM_TO_INTERNAL_FACTOR } from '../../utils/constants';
import { calculateOuterOutline, getStyle, parseCss } from '../../utils/svgUtils';
import RoomComponentDimensionsIndicator from './indicators/RoomComponentDimensionsIndicator';
import RotationSprite from './RotationSprite';

interface SVGLoaderProps {
  svgPath: string;
  position?: [number, number, number];
  length: number;
  depth: number;
  rotation: number;
  fillColor?: string;
  strokeColor?: string;
  strokeWidth?: number;
  texture?: ITexture;
  index: number;
  isSelected: boolean;
  interactionMode: EInteractionMode;
  showRoomComponentDetails?: boolean;
  onClick: (e: ThreeEvent<PointerEvent>) => void;
  onPositionChange?: (newPosition: [number, number, number]) => void;
  onDragMove?: (newPosition: [number, number, number]) => void;
  onRotationChange?: (newRotation: number) => void;
  customHandleClick?: (e: ThreeEvent<PointerEvent>, isDragging: boolean) => void;
  onDragEnd?: (finalPosition: THREE.Vector3) => void;
  onFootprintChange?: (footprint: { width: number; length: number }) => void;
  isGhost?: boolean;
}

type SvgPathRenderData = {
  key: string;
  path: ReturnType<typeof ThreeSVGLoader.prototype.parse>['paths'][number];
  geometries: THREE.BufferGeometry[];
  className: string;
  inlineFill?: string;
  inlineStroke?: string;
  inlineStrokeWidth?: string;
};

type SvgRenderData = {
  paths: SvgPathRenderData[];
  globalCenter: THREE.Vector2;
  sourceSize: THREE.Vector2;
};

type FillGeometryGroup = {
  key: string;
  color: string;
  geometry: THREE.BufferGeometry;
};

type StrokeLineGroup = {
  key: string;
  color: string;
  lineWidth: number;
  points: THREE.Vector3[];
};

const disableRaycast = () => null;

const SVGLoader: React.FC<SVGLoaderProps> = ({
  svgPath,
  position = [0, 0, 0],
  length,
  depth,
  rotation,
  fillColor,
  strokeColor,
  strokeWidth,
  texture,
  index,
  isSelected,
  interactionMode,
  showRoomComponentDetails,
  onClick,
  onPositionChange,
  onDragMove,
  onRotationChange,
  customHandleClick,
  onDragEnd,
  onFootprintChange,
  isGhost = false,
}) => {
  const pivotRef = useRef<Group>(null);
  const helperRef = useRef<Group>(null);
  const svgRef = useRef<Group>(null);
  const prevRotation = useRef<number>(rotation);
  const isDraggableRef = useRef(true);
  const pendingDragMoveRef = useRef<[number, number, number] | null>(null);
  const dragMoveFrameRef = useRef<number | null>(null);
  const { isActiveCoarse } = usePointerKind();

  const [isHovered, setIsHovered] = useState(false);
  const [rotationKey, setRotationKey] = useState(0);
  const [showRotationSprite, setShowRotationSprite] = useState(true);

  const onTopValue = 5;
  const svg = useLoader(ThreeSVGLoader, svgPath);
  const svgRenderData = useMemo(() => buildSvgRenderData(svg.paths), [svg]);

  const constrainDragPosition = useCallback((newPosition: THREE.Vector3) => {
    newPosition.z = onTopValue;
    return newPosition;
  }, []);

  const flushPendingDragMove = useCallback(() => {
    if (dragMoveFrameRef.current !== null) {
      cancelAnimationFrame(dragMoveFrameRef.current);
      dragMoveFrameRef.current = null;
    }

    const pendingPosition = pendingDragMoveRef.current;
    pendingDragMoveRef.current = null;

    if (pendingPosition) onDragMove?.(pendingPosition);
  }, [onDragMove]);

  const handleDragMove = useCallback((newPosition: THREE.Vector3) => {
    if (!onDragMove) return;

    pendingDragMoveRef.current = [newPosition.x, newPosition.y, 0];

    if (dragMoveFrameRef.current !== null) return;

    dragMoveFrameRef.current = requestAnimationFrame(() => {
      dragMoveFrameRef.current = null;
      const pendingPosition = pendingDragMoveRef.current;
      pendingDragMoveRef.current = null;

      if (pendingPosition) onDragMove(pendingPosition);
    });
  }, [onDragMove]);

  const handleDragEnd = useCallback((finalPosition: THREE.Vector3) => {
    flushPendingDragMove();

    const finalPlanPosition = new THREE.Vector3(finalPosition.x, finalPosition.y, 0);
    onPositionChange?.([finalPlanPosition.x, finalPlanPosition.y, finalPlanPosition.z]);
    onDragEnd?.(finalPlanPosition);
  }, [flushPendingDragMove, onDragEnd, onPositionChange]);

  useEffect(() => () => {
    if (dragMoveFrameRef.current !== null) {
      cancelAnimationFrame(dragMoveFrameRef.current);
      dragMoveFrameRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    svgRenderData.paths.forEach((pathData) => {
      pathData.geometries.forEach((geometry) => geometry.dispose());
    });
  }, [svgRenderData]);

  const svgStyles = useMemo(() => {
    const styleTag = svg.xml?.querySelector('style');
    return styleTag ? parseCss(styleTag.textContent || '') : {};
  }, [svg]);
  const svgFill = useMemo(() => {
    const fillAttr = svg.xml?.documentElement?.getAttribute('fill');
    return fillAttr && fillAttr !== 'none' ? fillAttr : null;
  }, [svg]);

  const { scaleX, scaleY } = useMemo(() => {
    const widthInUnits = length * MM_TO_INTERNAL_FACTOR;
    const depthInUnits = depth * MM_TO_INTERNAL_FACTOR;

    return {
      scaleX: svgRenderData.sourceSize.x > 0 ? widthInUnits / svgRenderData.sourceSize.x : 1,
      scaleY: svgRenderData.sourceSize.y > 0 ? depthInUnits / svgRenderData.sourceSize.y : 1,
    };
  }, [depth, length, svgRenderData]);

  const interactionHitboxArgs = useMemo<[number, number]>(() => [
    Math.max(svgRenderData.sourceSize.x, 1),
    Math.max(svgRenderData.sourceSize.y, 1),
  ], [svgRenderData.sourceSize.x, svgRenderData.sourceSize.y]);
  const interactionHitboxMaterial = useMemo(() => new THREE.MeshBasicMaterial({
    transparent: true,
    opacity: 0,
    depthWrite: false,
    side: DoubleSide,
  }), []);

  useEffect(() => () => {
    interactionHitboxMaterial.dispose();
  }, [interactionHitboxMaterial]);

  useEffect(() => {
    if (texture?.material && length > 0 && depth > 0) {
      const tex = texture.material;

      tex.repeat.set(scaleX, -scaleY);

      if (texture.offset !== undefined) tex.offset.x = -texture.offset;
      if (texture.rotation !== undefined) tex.rotation = texture.rotation;
      tex.needsUpdate = true;
    }
  }, [texture, length, depth, scaleX, scaleY]);

  // Update rotation
  useEffect(() => {
    if (helperRef.current) {
      helperRef.current.rotation.z = -rotation;
      if (prevRotation.current !== rotation) {
        setRotationKey((prev) => prev + 1);
        prevRotation.current = rotation;
      }
    }
  }, [rotation]);

  // Dragging logic
  const { isDragging } = useDraggableMesh(
    helperRef,
    constrainDragPosition,
    handleDragMove,
    isDraggableRef.current && (isHovered || isActiveCoarse) && !isGhost,
    true,
    1,
    handleDragEnd,
  );

  // Update position for stacking
  useEffect(() => {
    if (helperRef.current) {
      helperRef.current.position.set(position[0], position[1], isSelected || isDragging ? onTopValue : position[2] + index * 0.001);
    }
  }, [isSelected, isDragging, index, position, onTopValue]);

  useEffect(() => {
    setShowRotationSprite(!isDragging);
  }, [isDragging]);

  // Rotation end handler
  const handleRotationEnd = () => {
    if (helperRef.current) {
      const finalRot = -helperRef.current.rotation.z;
      const normRot = ((finalRot % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
      if (normRot !== prevRotation.current) {
        prevRotation.current = normRot;
        onRotationChange?.(normRot);
      }
    }
  };

  // Click handler
  const handleClick = (event: ThreeEvent<PointerEvent>) => {
    if (isGhost) return;
    if (event.button !== 0) return;
    event.stopPropagation();
    if (customHandleClick) {
      customHandleClick(event, isDragging);
    } else if (interactionMode !== EInteractionMode.MOVE) {
      onClick(event);
    }
  };

  const textureToUse = useMemo(() => texture?.material || undefined, [texture]);
  const hasTextureMap = Boolean(textureToUse);

  const fillGroups = useMemo(() => {
    const geometryGroups = new Map<string, { color: string; geometries: THREE.BufferGeometry[] }>();

    svgRenderData.paths.forEach((pathData) => {
      const fillFromClass = getStyle(svgStyles, pathData.className, 'fill');
      const fill = pathData.inlineFill && pathData.inlineFill !== 'none'
        ? pathData.inlineFill
        : fillFromClass && fillFromClass !== 'none'
          ? fillFromClass
          : svgFill || fillColor || 'none';

      if (fill === 'none' || pathData.geometries.length === 0) return;

      const color = hasTextureMap ? '#ffffff' : replaceCurrentColor(texture?.color || fill);
      const key = hasTextureMap ? '__texture__' : color;
      const group = geometryGroups.get(key) || { color, geometries: [] };
      group.geometries.push(...pathData.geometries);
      geometryGroups.set(key, group);
    });

    const groups: FillGeometryGroup[] = [];
    geometryGroups.forEach((group, key) => {
      const geometry = group.geometries.length === 1
        ? group.geometries[0].clone()
        : mergeBufferGeometries(group.geometries, false);

      if (geometry) {
        groups.push({ key, color: group.color, geometry });
      }
    });

    return groups;
  }, [fillColor, hasTextureMap, svgFill, svgRenderData, svgStyles, texture?.color]);

  useEffect(() => () => {
    fillGroups.forEach((group) => group.geometry.dispose());
  }, [fillGroups]);

  const strokeGroups = useMemo(() => {
    const groups = new Map<string, StrokeLineGroup>();

    svgRenderData.paths.forEach((pathData) => {
      const strokeFromClass = getStyle(svgStyles, pathData.className, 'stroke');
      const stroke = pathData.inlineStroke && pathData.inlineStroke !== 'none'
        ? pathData.inlineStroke
        : strokeFromClass && strokeFromClass !== 'none'
          ? strokeFromClass
          : strokeColor || 'none';
      const strokeWidthFromClass = getStyle(svgStyles, pathData.className, 'stroke-width');
      const finalStrokeWidth = parseFloat(pathData.inlineStrokeWidth || strokeWidthFromClass || strokeWidth || '1.5');

      if (stroke !== 'none') {
        const color = replaceCurrentColor(stroke);
        const key = `${color}-${finalStrokeWidth}`;
        const group = groups.get(key) || { key, color, lineWidth: finalStrokeWidth, points: [] };

        pathData.path.subPaths.forEach((subPath) => {
          const pts = subPath.getPoints(50);
          const centeredPts = centerOutlinePoints(pts, svgRenderData.globalCenter);
          for (let i = 0; i < centeredPts.length - 1; i++) {
            group.points.push(centeredPts[i], centeredPts[i + 1]);
          }
        });

        if (group.points.length > 0) {
          groups.set(key, group);
        }
      }
    });

    return Array.from(groups.values());
  }, [strokeColor, strokeWidth, svgRenderData, svgStyles]);

  // Precompute outline points
  const outlinePoints = useMemo(() => {
    const points = convertTuplesToVector2(calculateOuterOutline(svg.paths));
    return centerOutlinePoints(points, svgRenderData.globalCenter);
  }, [svg, svgRenderData.globalCenter]);
  const interactionHitboxGeometry = useMemo(() => {
    const hitboxOutline = isClosedOutline(outlinePoints) ? outlinePoints.slice(0, -1) : outlinePoints;

    if (hitboxOutline.length >= 3) {
      const shape = new THREE.Shape(hitboxOutline.map((point) => new THREE.Vector2(point.x, point.y)));
      return new THREE.ShapeGeometry(shape);
    }

    return new THREE.PlaneGeometry(interactionHitboxArgs[0], interactionHitboxArgs[1]);
  }, [interactionHitboxArgs, outlinePoints]);

  useEffect(() => () => {
    interactionHitboxGeometry.dispose();
  }, [interactionHitboxGeometry]);

  useEffect(() => {
    if (!onFootprintChange || outlinePoints.length === 0) return;

    const box = new THREE.Box3().setFromPoints(outlinePoints);
    const size = new THREE.Vector3();
    box.getSize(size);

    const widthWorld = size.x * scaleX;
    const lengthWorld = size.y * Math.abs(scaleY);

    onFootprintChange({ width: widthWorld, length: lengthWorld });
  }, [outlinePoints, scaleX, scaleY, onFootprintChange, svgPath]);

  return (
    <group ref={pivotRef} renderOrder={isSelected ? 9998 : 99 + index}>
      <group ref={helperRef} position={[position[0], position[1], position[2]]}>
        {!isGhost && showRoomComponentDetails && <RoomComponentDimensionsIndicator length={length} depth={depth} rotation={rotation} />}

        <group
          ref={svgRef}
          scale={[scaleX, -scaleY, 1]}
          onPointerDown={handleClick}
          onPointerEnter={(e) => {
            if (isGhost) return;
            e.stopPropagation();
            setIsHovered(true);
          }}
          onPointerLeave={() => setIsHovered(false)}
          raycast={isGhost ? () => null : undefined}
        >
          {!isGhost && (
            <mesh name="svg-interaction-hitbox" position={[0, 0, 0.01]} geometry={interactionHitboxGeometry} material={interactionHitboxMaterial} />
          )}

          <StaticSvgContent fillGroups={fillGroups} strokeGroups={strokeGroups} texture={textureToUse} isGhost={isGhost} />

          {!isGhost && isHovered && !isSelected && outlinePoints.length >= 2 && (
            <Line key={`hover-outline-${index}`} transparent points={outlinePoints} color="#33B2E9" lineWidth={2} scale={[1, 1, -1]} />
          )}
          {!isGhost && isSelected && outlinePoints.length >= 2 && (
            <Line key={`outline-${index}`} transparent points={outlinePoints} color="#009fe3" lineWidth={5} side={FrontSide} scale={[1, 1, -1]} />
          )}
        </group>
      </group>

      {!isGhost && isSelected && showRotationSprite && (
        <RotationSprite
          key={`${index}-${rotationKey}`}
          targetRef={helperRef}
          onStartRotate={() => {
            isDraggableRef.current = false;
          }}
          onEndRotate={() => {
            isDraggableRef.current = true;
            handleRotationEnd();
          }}
        />
      )}
    </group>
  );
};

const StaticSvgContent = memo(({
  fillGroups,
  strokeGroups,
  texture,
  isGhost,
}: {
  fillGroups: FillGeometryGroup[];
  strokeGroups: StrokeLineGroup[];
  texture?: THREE.Texture;
  isGhost: boolean;
}) => (
  <>
    {fillGroups.map((group) => (
      <mesh key={`fill-${group.key}`} geometry={group.geometry} raycast={disableRaycast}>
        <meshBasicMaterial attach="material" transparent={isGhost} opacity={isGhost ? 0.5 : 1} color={group.color} map={texture} side={FrontSide} />
      </mesh>
    ))}

    {strokeGroups.map((group) => (
      <Line
        key={`stroke-${group.key}`}
        points={group.points}
        segments
        color={group.color}
        lineWidth={group.lineWidth}
        side={FrontSide}
        scale={[1, 1, -1]}
        renderOrder={1000}
        raycast={disableRaycast}
      />
    ))}
  </>
));

function buildSvgRenderData(paths: ReturnType<typeof ThreeSVGLoader.prototype.parse>['paths']): SvgRenderData {
  const globalBox = new THREE.Box2();
  const lineFallbackBox = new THREE.Box2();
  let hasShapeBounds = false;
  let hasLineBounds = false;

  const shapeGroups = paths.map((path) => {
    const shapes = ThreeSVGLoader.createShapes(path);

    shapes.forEach((shape) => {
      const geometry = new THREE.ShapeGeometry(shape);
      geometry.computeBoundingBox();

      if (geometry.boundingBox) {
        globalBox.union(new THREE.Box2(
          new THREE.Vector2(geometry.boundingBox.min.x, geometry.boundingBox.min.y),
          new THREE.Vector2(geometry.boundingBox.max.x, geometry.boundingBox.max.y),
        ));
        hasShapeBounds = true;
      }

      geometry.dispose();
    });

    if (!hasShapeBounds) {
      path.subPaths.forEach((subPath) => {
        subPath.getPoints(100).forEach((point) => {
          lineFallbackBox.expandByPoint(point);
          hasLineBounds = true;
        });
      });
    }

    return { path, shapes };
  });

  const sourceBox = hasShapeBounds ? globalBox : lineFallbackBox;
  const globalCenter = new THREE.Vector2();
  const sourceSize = new THREE.Vector2(1, 1);

  if (hasShapeBounds || hasLineBounds) {
    sourceBox.getCenter(globalCenter);
    sourceBox.getSize(sourceSize);
  }

  const renderPaths = shapeGroups.map(({ path, shapes }, index) => ({
    key: `${index}`,
    path,
    geometries: shapes.map((shape) => {
      const geometry = new THREE.ShapeGeometry(shape);
      geometry.translate(-globalCenter.x, -globalCenter.y, 0);
      return geometry;
    }),
    className: path.userData?.node?.getAttribute('class') || '',
    inlineFill: replaceCurrentColor(path.userData?.node?.getAttribute('fill') || undefined),
    inlineStroke: path.userData?.node?.getAttribute('stroke') || undefined,
    inlineStrokeWidth: path.userData?.node?.getAttribute('stroke-width') || undefined,
  }));

  return {
    paths: renderPaths,
    globalCenter,
    sourceSize,
  };
}

function convertTuplesToVector2(points: [number, number, number][]): THREE.Vector2[] {
  return points.map((p) => new THREE.Vector2(p[0], p[1]));
}

function centerOutlinePoints(points: THREE.Vector2[], globalCenter: THREE.Vector2): THREE.Vector3[] {
  return points.map((p) => new THREE.Vector3(p.x - globalCenter.x, p.y - globalCenter.y, 0));
}

function isClosedOutline(points: THREE.Vector3[]) {
  if (points.length < 2) return false;
  return points[0].distanceToSquared(points[points.length - 1]) < 0.000001;
}

function replaceCurrentColor(value: string | undefined, fallback = '#000000') {
  if (value === 'currentColor') return fallback;
  return value;
}

export default SVGLoader;
